import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { apiClient } from '../utils/apiClient';
import { useAuth } from '../contexts/AuthContext';
import { 
    Users, Search, CheckSquare, Square, CheckCircle, XCircle, 
    AlertCircle, FileText, Calendar, Edit3, Loader2, Info,
    History, ChevronRight,
    ChevronUp, ChevronDown, FileSpreadsheet, Download, ShieldAlert, CreditCard,
    RotateCcw, ClipboardPaste, Eye, Briefcase, LogIn, LogOut
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://localhost:7197/api';

function getTodayLocalStr() {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

export function BulkOperationsPage() {
    const { permissions, firmCode } = useAuth();
    
    const hasAccess = permissions?.includes('bulk_operations:access');
    const canManual = permissions?.includes('bulk_operations:manage_manual');
    const canLeaves = permissions?.includes('bulk_operations:manage_leaves');
    const canAccessOps = permissions?.includes('bulk_operations:access');
    const canScheduleOps = permissions?.includes('bulk_operations:access') || permissions?.includes('assignments:create');
    
    // States
    const [toastMessage, setToastMessage] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [leaveTypes, setLeaveTypes] = useState([]);
    const [accessGroups, setAccessGroups] = useState([]);
    const [workPeriods, setWorkPeriods] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Left Panel State
    const [searchTerm, setSearchTerm] = useState('');
    const [departmentFilter, setDepartmentFilter] = useState('ALL');
    const [selectionFilter, setSelectionFilter] = useState('ALL'); // 'ALL' | 'SELECTED' | 'UNSELECTED'
    const [selectedEmpIds, setSelectedEmpIds] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);

    // Quick Paste Modal State (Sicil / TC / İsim Yapıştır)
    const [pasteModalOpen, setPasteModalOpen] = useState(false);
    const [pasteText, setPasteText] = useState('');
    const [pasteSummary, setPasteSummary] = useState(null);
    
    // Tab State
    const [activeTab, setActiveTab] = useState('new'); // 'new' | 'history'
    
    // History State
    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [selectedHistory, setSelectedHistory] = useState(null);
    const [historyDetailLoading, setHistoryDetailLoading] = useState(false);
    const [historyPage, setHistoryPage] = useState(1);
    const [historyPageSize, setHistoryPageSize] = useState(20);
    const [historyTotal, setHistoryTotal] = useState(0);
    const [historySortConfig, setHistorySortConfig] = useState({ key: 'CreatedAt', direction: 'desc' });
    const [undoingId, setUndoingId] = useState(null);

    // Right Panel State
    const [operationType, setOperationType] = useState('manual'); // 'manual' | 'leave' | 'access' | 'workSchedule' | 'credentials'
    const [submitting, setSubmitting] = useState(false);
    
    // Forms State
    const [manualForm, setManualForm] = useState({
        recordTime: '',
        recordType: 0, // 0: Giriş, 1: Çıkış
        deviceSerialNumber: 'Sistem-Toplu',
        notes: ''
    });

    const [leaveForm, setLeaveForm] = useState({
        izinTuruId: '',
        startDate: getTodayLocalStr(),
        endDate: getTodayLocalStr(),
        startTime: '09:00',
        endTime: '18:00',
        aciklama: '',
        autoApprove: false,
        useUnit: 1 // 1: Gun, 2: Saat
    });

    const [accessForm, setAccessForm] = useState({
        accessGroupId: '',
        assignmentType: 2, // 1: Replace, 2: Append
        priorityMode: -1
    });

    const [scheduleForm, setScheduleForm] = useState({
        workPeriodId: '',
        isPermanent: true,
        startDate: getTodayLocalStr(),
        endDate: getTodayLocalStr()
    });

    const [credentialForm, setCredentialForm] = useState({
        credentialType: 5,
        overwriteExisting: false
    });

    // Result Modal State
    const [resultData, setResultData] = useState(null);
    const [reportFilter, setReportFilter] = useState('all');
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [liveProgress, setLiveProgress] = useState(null);

    // O(1) Lookup Set for large employee selections (10k-50k employees)
    const selectedEmpIdSet = useMemo(() => new Set(selectedEmpIds), [selectedEmpIds]);

    const fetchData = async () => {
        try {
            const empRes = await apiClient('Employee');
            if (empRes) {
                const empData = Array.isArray(empRes) ? empRes : empRes.data || [];
                setEmployees(empData
                    .filter(e => e.isActive !== false && !e.isDeleted)
                    .map(e => ({
                        ...e,
                        sicilNo: e.turkishIdNo || e.registrationNumber || e.sicilNo || '',
                        deptLabel: e.departmentName || e.department?.name || e.departmentCode || 'Departmansız'
                    })));
            }

            if (canLeaves) {
                try {
                    const ltRes = await apiClient('izinturu/dropdown');
                    if (ltRes) setLeaveTypes(Array.isArray(ltRes) ? ltRes : ltRes.data || []);
                } catch (e) { console.error(e); }
            }

            if (canAccessOps) {
                try {
                    const agRes = await apiClient('access-groups/dropdown');
                    if (agRes) setAccessGroups(Array.isArray(agRes) ? agRes : agRes.data || []);
                } catch(e) { console.error(e); }
            }

            if (canScheduleOps) {
                try {
                    const wpRes = await apiClient('bulk-operations/work-periods');
                    if (wpRes) setWorkPeriods(Array.isArray(wpRes) ? wpRes : wpRes.data || []);
                } catch (e) {
                    try {
                        const fallbackWp = await apiClient('scheduling/periods');
                        if (fallbackWp) setWorkPeriods(Array.isArray(fallbackWp) ? fallbackWp : fallbackWp.data || []);
                    } catch (err2) { console.error(err2); }
                }
            }
        } catch (err) {
            console.error("Veri çekilirken hata:", err);
            setToastMessage({ type: 'error', text: "Gerekli veriler yüklenemedi. Lütfen sayfayı yenileyin." });
        } finally {
            setLoading(false);
        }
    };

    const fetchHistory = useCallback(async () => {
        setHistoryLoading(true);
        try {
            const isDesc = historySortConfig.direction === 'desc';
            const res = await apiClient(`bulk-operations/history?page=${historyPage}&pageSize=${historyPageSize}&sortBy=${historySortConfig.key}&isDescending=${isDesc}`);
            if (res) {
                setHistory(res.items || []);
                setHistoryTotal(res.totalCount || 0);
            }
        } catch (err) {
            console.error("Geçmiş çekilirken hata:", err);
            setToastMessage({ type: 'error', text: "İşlem geçmişi yüklenemedi." });
        } finally {
            setHistoryLoading(false);
        }
    }, [historyPage, historyPageSize, historySortConfig]);

    const handleHistorySort = (key) => {
        setHistorySortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
        }));
        setHistoryPage(1);
    };

    const fetchHistoryDetail = async (id) => {
        setHistoryDetailLoading(true);
        try {
            const res = await apiClient(`bulk-operations/history/${id}`);
            if (res) {
                setSelectedHistory(res);
            }
        } catch (err) {
            setToastMessage({ type: 'error', text: "İşlem detayı yüklenemedi." });
        } finally {
            setHistoryDetailLoading(false);
        }
    };

    const handleUndoOperation = async (historyItem) => {
        if (!window.confirm(`#${historyItem.id} numaralı toplu işlemi geri almak istediğinize emin misiniz?\n\n• Manuel kayıtlar yasal denetim iziyle 'Yoksayıldı' olarak işaretlenecek,\n• Toplu izinler iptal edilerek düşülen bakiyeler iade edilecek,\n• Toplu mesai atamaları geri çekilecek ve puantaj otomatik yeniden hesaplanacaktır.`)) {
            return;
        }
        setUndoingId(historyItem.id);
        try {
            const res = await apiClient(`bulk-operations/history/${historyItem.id}/undo`, { method: 'POST' });
            setToastMessage({ type: 'success', text: res?.message || 'Toplu işlem başarıyla geri alındı.' });
            await fetchHistory();
            if (selectedHistory && selectedHistory.id === historyItem.id) {
                await fetchHistoryDetail(historyItem.id);
            }
        } catch (err) {
            setToastMessage({ type: 'error', text: err.message || 'İşlem geri alınırken hata oluştu.' });
        } finally {
            setUndoingId(null);
        }
    };

    const handleExport = async (id, format) => {
        try {
            const token = localStorage.getItem('token');
            const url = `${API_BASE}/bulk-operations/history/${id}/${format}`;
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'X-Firm-Code': firmCode || localStorage.getItem('firmCode') || ''
                }
            });

            if (!response.ok) throw new Error('Dosya indirilemedi.');

            const contentDisposition = response.headers.get('Content-Disposition');
            let filename = `Toplu_Islem_Raporu_${id}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename\*?=['"]?(?:UTF-\d['"]*)?([^;\r\n"']*)['"]?;?/i);
                if (filenameMatch && filenameMatch[1]) {
                    filename = decodeURIComponent(filenameMatch[1]);
                }
            }

            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            console.error('Export error:', error);
            setToastMessage({ type: 'error', text: 'Rapor indirilirken bir hata oluştu.' });
        }
    };

    useEffect(() => {
        if (!hasAccess) {
            setLoading(false);
            return;
        }

        if (!canManual && canLeaves) {
            setOperationType('leave');
        } else if (!canManual && !canLeaves && canAccessOps) {
            setOperationType('access');
        }

        const now = new Date();
        now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
        setManualForm(f => ({ ...f, recordTime: now.toISOString().slice(0, 16) }));

        fetchData();
    }, [hasAccess, canManual, canLeaves, canAccessOps]);

    useEffect(() => {
        if (activeTab === 'history' && hasAccess) {
            fetchHistory();
        }
    }, [activeTab, hasAccess, fetchHistory]);

    useEffect(() => {
        if (toastMessage) {
            const timer = setTimeout(() => setToastMessage(null), 5000);
            return () => clearTimeout(timer);
        }
    }, [toastMessage]);

    const departmentOptions = useMemo(() => {
        const set = new Set();
        employees.forEach(e => {
            if (e.deptLabel) set.add(e.deptLabel);
        });
        return Array.from(set).sort((a, b) => a.localeCompare(b, 'tr'));
    }, [employees]);

    // Filtered Employees (Search + Department + Selection State)
    const filteredEmployees = useMemo(() => {
        const q = searchTerm.trim().toLowerCase();
        return employees.filter(e => {
            if (departmentFilter !== 'ALL' && e.deptLabel !== departmentFilter) {
                return false;
            }
            if (selectionFilter === 'SELECTED' && !selectedEmpIdSet.has(e.id)) {
                return false;
            }
            if (selectionFilter === 'UNSELECTED' && selectedEmpIdSet.has(e.id)) {
                return false;
            }
            if (!q) return true;
            const fullName = `${e.firstName || ''} ${e.lastName || ''}`.toLowerCase();
            return fullName.includes(q) ||
                   (e.sicilNo && e.sicilNo.toLowerCase().includes(q)) ||
                   (e.deptLabel && e.deptLabel.toLowerCase().includes(q)) ||
                   (e.departmentCode && e.departmentCode.toLowerCase().includes(q)) ||
                   (e.positionName && e.positionName.toLowerCase().includes(q)) ||
                   String(e.id) === q;
        });
    }, [employees, searchTerm, departmentFilter, selectionFilter, selectedEmpIdSet]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, departmentFilter, selectionFilter]);

    const totalPages = Math.ceil(filteredEmployees.length / pageSize) || 1;
    const paginatedEmployees = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredEmployees.slice(start, start + pageSize);
    }, [filteredEmployees, currentPage, pageSize]);

    const handleSelectAllFiltered = () => {
        const filteredIds = filteredEmployees.map(e => e.id);
        const allFilteredSelected = filteredIds.length > 0 && filteredIds.every(id => selectedEmpIdSet.has(id));
        if (allFilteredSelected) {
            const removeSet = new Set(filteredIds);
            setSelectedEmpIds(prev => prev.filter(id => !removeSet.has(id)));
        } else {
            const merged = new Set([...selectedEmpIds, ...filteredIds]);
            setSelectedEmpIds(Array.from(merged));
        }
    };

    const handleToggleEmp = (id) => {
        setSelectedEmpIds(prev => {
            if (selectedEmpIdSet.has(id)) {
                return prev.filter(item => item !== id);
            }
            return [...prev, id];
        });
    };

    const handleApplyPasteSelection = () => {
        const rawTokens = pasteText
            .split(/[\r\n,;\t]+/)
            .map(s => s.trim())
            .filter(Boolean);

        if (rawTokens.length === 0) {
            setToastMessage({ type: 'error', text: 'Lütfen en az bir T.C. Kimlik No, Sicil No veya İsim yapıştırın.' });
            return;
        }

        const matchedIds = new Set(selectedEmpIds);
        const matchedNames = [];
        const unmatchedTokens = [];

        rawTokens.forEach(token => {
            const lower = token.toLowerCase();
            const found = employees.find(e =>
                (e.sicilNo && e.sicilNo.toLowerCase() === lower) ||
                (e.turkishIdNo && e.turkishIdNo.toLowerCase() === lower) ||
                (e.registrationNumber && e.registrationNumber.toLowerCase() === lower) ||
                String(e.id) === lower ||
                `${e.firstName || ''} ${e.lastName || ''}`.trim().toLowerCase() === lower
            );
            if (found) {
                matchedIds.add(found.id);
                matchedNames.push(`${found.firstName} ${found.lastName}`);
            } else {
                unmatchedTokens.push(token);
            }
        });

        setSelectedEmpIds(Array.from(matchedIds));
        setPasteSummary({
            totalInput: rawTokens.length,
            matchedCount: matchedNames.length,
            unmatchedTokens
        });

        if (matchedNames.length > 0) {
            setToastMessage({ type: 'success', text: `${matchedNames.length} personel listeye eklendi ve seçildi.` });
        }
    };

    const selectedLeaveTypeObj = useMemo(() => {
        return leaveTypes.find(lt => lt.id === Number(leaveForm.izinTuruId));
    }, [leaveTypes, leaveForm.izinTuruId]);

    const selectedWorkPeriodObj = useMemo(() => {
        return workPeriods.find(wp => wp.id === Number(scheduleForm.workPeriodId));
    }, [workPeriods, scheduleForm.workPeriodId]);

    useEffect(() => {
        if (selectedLeaveTypeObj) {
            if (selectedLeaveTypeObj.kullanimBirimi === 1 || selectedLeaveTypeObj.kullanimBirimi === 0) {
                setLeaveForm(f => ({ ...f, useUnit: 1 }));
            } else if (selectedLeaveTypeObj.kullanimBirimi === 2) {
                setLeaveForm(f => ({ ...f, useUnit: 2 }));
            }
        }
    }, [selectedLeaveTypeObj]);

    const validateForm = () => {
        if (selectedEmpIds.length === 0) {
            setToastMessage({ type: 'error', text: 'Lütfen sol listeden en az bir personel seçin.' });
            return false;
        }

        if (operationType === 'manual') {
            if (!manualForm.recordTime) {
                setToastMessage({ type: 'error', text: 'Geçiş zamanı zorunludur.' });
                return false;
            }
            if (!manualForm.notes || manualForm.notes.trim().length < 3) {
                setToastMessage({ type: 'error', text: 'Açıklama alanı en az 3 karakter olmalıdır.' });
                return false;
            }
        } else if (operationType === 'leave') {
            if (!leaveForm.izinTuruId) {
                setToastMessage({ type: 'error', text: 'İzin türü seçilmelidir.' });
                return false;
            }
            if (!leaveForm.startDate || !leaveForm.endDate) {
                setToastMessage({ type: 'error', text: 'Başlangıç ve bitiş tarihleri zorunludur.' });
                return false;
            }
            if (!leaveForm.aciklama || leaveForm.aciklama.trim().length < 3) {
                setToastMessage({ type: 'error', text: 'İzin açıklaması en az 3 karakter olmalıdır.' });
                return false;
            }
        } else if (operationType === 'access') {
            if (!accessForm.accessGroupId) {
                setToastMessage({ type: 'error', text: 'Yetki grubu seçilmelidir.' });
                return false;
            }
        } else if (operationType === 'workSchedule') {
            if (!scheduleForm.workPeriodId) {
                setToastMessage({ type: 'error', text: 'Lütfen atanacak Çalışma Periyodunu (Mesai Takvimini) seçin.' });
                return false;
            }
            if (!scheduleForm.startDate) {
                setToastMessage({ type: 'error', text: 'Başlangıç tarihi zorunludur.' });
                return false;
            }
            if (!scheduleForm.isPermanent && (!scheduleForm.endDate || scheduleForm.endDate < scheduleForm.startDate)) {
                setToastMessage({ type: 'error', text: 'Geçici atamalarda bitiş tarihi başlangıç tarihinden önce olamaz.' });
                return false;
            }
        }
        return true;
    };

    const handlePreSubmit = () => {
        if (!validateForm()) return;
        setConfirmOpen(true);
    };

    const handleExecute = async (isDryRun = false) => {
        if (isDryRun && !validateForm()) return;
        setConfirmOpen(false);
        setSubmitting(true);
        setResultData(null);

        const opKey = `bulk_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        const totalSelected = selectedEmpIds.length;

        setLiveProgress({
            percent: 6,
            processedCount: 0,
            totalCount: totalSelected,
            successCount: 0,
            failedCount: 0,
            stageTitle: isDryRun ? 'Ön Kontrol (Simülasyon) Başlatıldı' : 'Toplu İşlem Başlatıldı',
            stageDetail: `${totalSelected} personel için yetki, vardiya ve kural verileri hazırlanıyor...`,
            isDryRun
        });

        let pollActive = true;
        const pollInterval = setInterval(async () => {
            if (!pollActive) return;
            try {
                const prog = await apiClient(`bulk-operations/progress/${opKey}`);
                if (prog && pollActive) {
                    setLiveProgress(prev => {
                        if (!prev) return prev;
                        const nextPct = Math.max(prev.percent, prog.percent || prev.percent);
                        return {
                            ...prev,
                            percent: nextPct < 94 && nextPct === prev.percent ? Math.min(94, prev.percent + 3) : nextPct,
                            processedCount: prog.processedCount ?? prev.processedCount,
                            totalCount: prog.totalCount || totalSelected,
                            successCount: prog.successCount ?? prev.successCount,
                            failedCount: prog.failedCount ?? prev.failedCount,
                            stageTitle: prog.stageTitle || prev.stageTitle,
                            stageDetail: prog.stageDetail || prev.stageDetail
                        };
                    });
                }
            } catch (_) {
                // Ignore transient progress poll errors
            }
        }, 220);

        try {
            let res;
            if (operationType === 'manual') {
                const payload = {
                    employeeIds: selectedEmpIds,
                    recordTime: new Date(manualForm.recordTime).toISOString(),
                    recordType: Number(manualForm.recordType),
                    deviceSerialNumber: manualForm.deviceSerialNumber,
                    notes: manualForm.notes,
                    dryRun: isDryRun,
                    operationKey: opKey
                };
                res = await apiClient('bulk-operations/manual-records', {
                    method: 'POST',
                    body: JSON.stringify(payload)
                });
            } else if (operationType === 'leave') {
                const isHourly = leaveForm.useUnit === 2;
                let sDateTime = null;
                let eDateTime = null;

                if (isHourly) {
                    sDateTime = `${leaveForm.startDate}T${leaveForm.startTime}:00`;
                    eDateTime = `${leaveForm.startDate}T${leaveForm.endTime}:00`;
                }

                const payload = {
                    employeeIds: selectedEmpIds,
                    izinTuruId: Number(leaveForm.izinTuruId),
                    baslangicTarihi: `${leaveForm.startDate}T00:00:00`,
                    bitisTarihi: `${isHourly ? leaveForm.startDate : leaveForm.endDate}T00:00:00`,
                    startDateTime: sDateTime,
                    endDateTime: eDateTime,
                    aciklama: leaveForm.aciklama,
                    autoApprove: leaveForm.autoApprove,
                    talepKullanimBirimi: selectedLeaveTypeObj?.kullanimBirimi === 3 ? Number(leaveForm.useUnit) : null,
                    dryRun: isDryRun,
                    operationKey: opKey
                };
                res = await apiClient('bulk-operations/leaves', {
                    method: 'POST',
                    body: JSON.stringify(payload)
                });
            } else if (operationType === 'access') {
                const payload = {
                    employeeIds: selectedEmpIds,
                    accessGroupId: accessForm.accessGroupId,
                    assignmentType: Number(accessForm.assignmentType),
                    priorityMode: Number(accessForm.priorityMode),
                    dryRun: isDryRun,
                    operationKey: opKey
                };
                res = await apiClient('bulk-operations/access-assignments', {
                    method: 'POST',
                    body: JSON.stringify(payload)
                });
            } else if (operationType === 'workSchedule') {
                const payload = {
                    employeeIds: selectedEmpIds,
                    workPeriodId: Number(scheduleForm.workPeriodId),
                    startDate: `${scheduleForm.startDate}T00:00:00`,
                    endDate: scheduleForm.isPermanent ? null : `${scheduleForm.endDate}T00:00:00`,
                    dryRun: isDryRun,
                    operationKey: opKey
                };
                res = await apiClient('bulk-operations/work-schedules', {
                    method: 'POST',
                    body: JSON.stringify(payload)
                });
            } else if (operationType === 'credentials') {
                const payload = {
                    employeeIds: selectedEmpIds,
                    credentialType: Number(credentialForm.credentialType),
                    overwriteExisting: credentialForm.overwriteExisting,
                    dryRun: isDryRun,
                    operationKey: opKey
                };
                res = await apiClient('bulk-operations/generate-credentials', {
                    method: 'POST',
                    body: JSON.stringify(payload)
                });
            }

            pollActive = false;
            clearInterval(pollInterval);

            setLiveProgress({
                percent: 100,
                processedCount: res?.total ?? totalSelected,
                totalCount: res?.total ?? totalSelected,
                successCount: res?.success ?? 0,
                failedCount: res?.failed ?? 0,
                stageTitle: isDryRun ? 'Ön Kontrol %100 Tamamlandı' : 'Toplu İşlem %100 Tamamlandı',
                stageDetail: `${res?.total ?? totalSelected} personel işlendi (${res?.success ?? 0} başarılı, ${res?.failed ?? 0} hatalı). Sonuç raporu açılıyor...`,
                isDryRun
            });

            await new Promise(resolve => setTimeout(resolve, 480));

            setResultData(res);
            setReportFilter(res?.failed > 0 ? 'error' : 'all');

            if (isDryRun) {
                setToastMessage({
                    type: 'info',
                    text: `Ön kontrol (simülasyon) tamamlandı: ${res.success} personel uygun, ${res.failed} personel uygun değil.`
                });
            } else if (res.success > 0 && res.failed === 0) {
                setToastMessage({ type: 'success', text: `Toplu işlem başarıyla tamamlandı (${res.success} personel).` });
            } else if (res.success > 0 && res.failed > 0) {
                setToastMessage({ type: 'info', text: `İşlem kısmen tamamlandı (${res.success} başarılı, ${res.failed} hatalı). Detayları rapordan inceleyin.` });
            } else {
                setToastMessage({ type: 'error', text: 'Toplu işlemde tüm kayıtlar başarısız oldu. Raporu inceleyin.' });
            }

        } catch (err) {
            pollActive = false;
            clearInterval(pollInterval);
            setToastMessage({ type: 'error', text: err.message || 'İşlem sırasında beklenmeyen bir hata oluştu.' });
        } finally {
            pollActive = false;
            clearInterval(pollInterval);
            setSubmitting(false);
            setLiveProgress(null);
        }
    };

    const getOperationTypeBadge = (opType) => {
        switch (opType) {
            case 'ManualRecord':
                return <span className="px-2 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300 border border-blue-200 dark:border-blue-500/30 rounded text-xs font-medium">Manuel Kayıt</span>;
            case 'Leave':
                return <span className="px-2 py-0.5 bg-purple-100 text-purple-700 dark:bg-purple-500/15 dark:text-purple-300 border border-purple-200 dark:border-purple-500/30 rounded text-xs font-medium">Toplu İzin</span>;
            case 'AccessAssignment':
                return <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-500/30 rounded text-xs font-medium">Yetki Gönderimi</span>;
            case 'WorkSchedule':
                return <span className="px-2 py-0.5 bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300 border border-amber-200 dark:border-amber-500/30 rounded text-xs font-medium">Mesai / Periyot Atama</span>;
            case 'BulkGenerateQR':
                return <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-500/30 rounded text-xs font-medium">Sanal QR Üretimi</span>;
            case 'BulkGenerateNFC':
                return <span className="px-2 py-0.5 bg-cyan-100 text-cyan-700 dark:bg-cyan-500/15 dark:text-cyan-300 border border-cyan-200 dark:border-cyan-500/30 rounded text-xs font-medium">Sanal NFC Üretimi</span>;
            default:
                return <span className="px-2 py-0.5 bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 rounded text-xs font-medium">{opType}</span>;
        }
    };

    const getHistoryStatusBadge = (status) => {
        if (status === 'Completed') {
            return <span className="px-2.5 py-0.5 bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300 rounded-full text-xs font-medium">Tamamlandı</span>;
        }
        if (status === 'PartiallyFailed') {
            return <span className="px-2.5 py-0.5 bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300 rounded-full text-xs font-medium">Kısmen Hatalı</span>;
        }
        if (status === 'Undone') {
            return <span className="px-2.5 py-0.5 bg-orange-100 text-orange-800 dark:bg-orange-500/15 dark:text-orange-300 border border-orange-200 dark:border-orange-500/30 rounded-full text-xs font-medium">Geri Alındı</span>;
        }
        return <span className="px-2.5 py-0.5 bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300 rounded-full text-xs font-medium">Başarısız</span>;
    };

    const canUndoHistoryItem = (item) => {
        if (!item || item.status === 'Undone' || item.successCount <= 0) return false;
        return ['ManualRecord', 'Leave', 'WorkSchedule'].includes(item.operationType);
    };

    if (loading) {
        return (
            <div className="flex h-[calc(100vh-120px)] items-center justify-center">
                <Loader2 className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
            </div>
        );
    }

    if (!hasAccess) {
        return (
            <div className="p-8 text-center h-[calc(100vh-120px)] flex flex-col items-center justify-center">
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-8 max-w-md shadow-sm">
                    <AlertCircle className="w-12 h-12 mb-3 text-red-500 mx-auto" />
                    <h1 className="text-xl font-bold text-gray-900 dark:text-white">Yetkisiz Erişim</h1>
                    <p className="mt-1 text-gray-500 dark:text-gray-400 text-sm">Toplu işlemler sayfasına erişim yetkiniz bulunmamaktadır.</p>
                </div>
            </div>
        );
    }

    const allFilteredSelected = filteredEmployees.length > 0 && filteredEmployees.every(e => selectedEmpIdSet.has(e.id));

    return (
        <div className="p-2 md:p-4 space-y-5 text-gray-900 dark:text-gray-100 relative">
            {/* Custom Toast */}
            {toastMessage && (
                <div className={`fixed top-4 right-4 z-50 p-4 rounded-xl shadow-2xl flex items-center gap-3 border transition-all duration-300 ${
                    toastMessage.type === 'error' ? 'bg-red-50 dark:bg-red-950/90 border-red-200 dark:border-red-500/50 text-red-800 dark:text-red-200' :
                    toastMessage.type === 'success' ? 'bg-emerald-50 dark:bg-emerald-950/90 border-emerald-200 dark:border-emerald-500/50 text-emerald-800 dark:text-emerald-200' :
                    'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-100'
                }`}>
                    {toastMessage.type === 'error' ? <XCircle className="w-5 h-5 text-red-500 dark:text-red-400 shrink-0" /> :
                     toastMessage.type === 'success' ? <CheckCircle className="w-5 h-5 text-emerald-500 dark:text-emerald-400 shrink-0" /> :
                     <Info className="w-5 h-5 text-blue-500 dark:text-blue-400 shrink-0" />}
                    <span className="text-sm font-medium">{toastMessage.text}</span>
                    <button onClick={() => setToastMessage(null)} className="ml-2 text-gray-400 hover:text-gray-700 dark:hover:text-white">✕</button>
                </div>
            )}

            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div>
                    <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2.5">
                        <Users className="w-6 h-6 text-gray-700 dark:text-gray-300" />
                        Toplu İşlemler & Kurumsal Operasyon Merkezi
                    </h1>
                    <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
                        Binlerce personel üzerinde tek adımda ön kontrollü (simülasyonlu) manuel geçiş, izin, mesai takvimi, kapı yetkisi ve sanal kart işlemleri uygulayın.
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                
                {/* SOL PANEL: PERSONEL SEÇİMİ VE KURUMSAL FİLTRELER */}
                <div className="lg:col-span-5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-4 flex flex-col h-[calc(100vh-170px)] shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                            Personel Seçimi
                            <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-600 px-2.5 py-0.5 rounded-full font-bold">
                                {selectedEmpIds.length} / {employees.length} Seçili
                            </span>
                        </h2>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => { setPasteSummary(null); setPasteModalOpen(true); }}
                                className="text-xs bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600 px-2.5 py-1 rounded-lg flex items-center gap-1 transition-colors font-medium"
                                title="Excel veya listeden Sicil / T.C. / İsim yapıştırarak toplu seç"
                            >
                                <ClipboardPaste className="w-3.5 h-3.5" />
                                Liste Yapıştır
                            </button>
                            {selectedEmpIds.length > 0 && (
                                <button
                                    onClick={() => setSelectedEmpIds([])}
                                    className="text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 px-2 py-1 rounded-lg bg-red-50/70 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 font-medium"
                                >
                                    Temizle
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Arama Kutusu */}
                    <div className="relative mb-2.5">
                        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
                        <input 
                            type="text" 
                            placeholder="İsim, T.C., Sicil No, Departman veya Pozisyon ara..." 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl pl-9 pr-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-400/30"
                        />
                    </div>

                    {/* Kurumsal Filtreler: Departman & Seçim Durumu */}
                    <div className="grid grid-cols-2 gap-2 mb-3">
                        <select
                            value={departmentFilter}
                            onChange={e => setDepartmentFilter(e.target.value)}
                            className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl px-2.5 py-1.5 text-xs text-gray-800 dark:text-gray-200 focus:outline-none"
                        >
                            <option value="ALL">Tüm Departmanlar ({departmentOptions.length})</option>
                            {departmentOptions.map(dept => (
                                <option key={dept} value={dept}>{dept}</option>
                            ))}
                        </select>

                        <select
                            value={selectionFilter}
                            onChange={e => setSelectionFilter(e.target.value)}
                            className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl px-2.5 py-1.5 text-xs text-gray-800 dark:text-gray-200 focus:outline-none"
                        >
                            <option value="ALL">Tüm Personeller ({employees.length})</option>
                            <option value="SELECTED">Sadece Seçililer ({selectedEmpIds.length})</option>
                            <option value="UNSELECTED">Seçilmeyenler ({Math.max(0, employees.length - selectedEmpIds.length)})</option>
                        </select>
                    </div>

                    <div className="flex items-center justify-between px-1 mb-2">
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                            Filtrelenen: <strong className="text-gray-900 dark:text-gray-100">{filteredEmployees.length}</strong> kişi
                        </span>
                        <button 
                            onClick={handleSelectAllFiltered}
                            className="text-xs text-gray-700 dark:text-gray-200 hover:underline flex items-center gap-1 font-semibold"
                        >
                            {allFilteredSelected ? 'Filtrelenenleri Kaldır' : `Filtrelenen Tümünü Seç (${filteredEmployees.length})`}
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto pr-1 space-y-1.5">
                        {paginatedEmployees.length === 0 ? (
                            <div className="text-center text-gray-400 dark:text-gray-500 py-8 text-sm">
                                Filtre kriterlerine uygun personel bulunamadı.
                            </div>
                        ) : (
                            paginatedEmployees.map(emp => {
                                const isSelected = selectedEmpIdSet.has(emp.id);
                                return (
                                    <div 
                                        key={emp.id}
                                        onClick={() => handleToggleEmp(emp.id)}
                                        className={`flex items-center gap-3 p-2.5 rounded-xl border cursor-pointer transition-colors ${
                                            isSelected 
                                            ? 'bg-emerald-50/70 dark:bg-emerald-950/25 border-emerald-300 dark:border-emerald-600/40 text-gray-900 dark:text-white' 
                                            : 'bg-gray-50/70 dark:bg-gray-900/60 border-gray-200/80 dark:border-gray-700/70 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                                        }`}
                                    >
                                        {isSelected ? (
                                            <CheckSquare className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                                        ) : (
                                            <Square className="w-5 h-5 text-gray-400 dark:text-gray-500 shrink-0" />
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium truncate">{emp.firstName} {emp.lastName}</p>
                                            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 truncate">
                                                <span>{emp.sicilNo || 'Sicil/TC Yok'}</span>
                                                <span>•</span>
                                                <span className="truncate">{emp.deptLabel}</span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {/* Pagination Footer */}
                    <div className="pt-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mt-2">
                        <div className="flex items-center gap-2">
                            <span>Gösterim:</span>
                            <select 
                                value={pageSize} 
                                onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                                className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1 text-gray-800 dark:text-gray-200 focus:outline-none"
                            >
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                                <option value={250}>250</option>
                            </select>
                        </div>
                        
                        <div className="flex items-center gap-2">
                            <button 
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                className="px-2.5 py-1 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
                            >
                                Önceki
                            </button>
                            <span>
                                Sayfa <strong className="text-gray-900 dark:text-white">{currentPage}</strong> / {totalPages}
                            </span>
                            <button 
                                disabled={currentPage === totalPages}
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                className="px-2.5 py-1 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
                            >
                                Sonraki
                            </button>
                        </div>
                    </div>
                </div>

                {/* SAĞ PANEL: İŞLEM DETAYLARI VE GEÇMİŞ */}
                <div className="lg:col-span-7 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-6 flex flex-col h-[calc(100vh-170px)] shadow-sm">
                    {/* ANA SEKMELER: YENİ İŞLEM vs İŞLEM GEÇMİŞİ */}
                    <div className="flex items-center gap-2 border-b border-gray-200 dark:border-gray-700 pb-4 mb-6">
                        <button
                            onClick={() => setActiveTab('new')}
                            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
                                activeTab === 'new' 
                                ? 'bg-gray-900 text-white dark:bg-gray-700 dark:text-white shadow-sm' 
                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700/60'
                            }`}
                        >
                            <Edit3 className="w-4 h-4" />
                            Yeni Toplu İşlem
                        </button>
                        <button
                            onClick={() => setActiveTab('history')}
                            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
                                activeTab === 'history' 
                                ? 'bg-gray-900 text-white dark:bg-gray-700 dark:text-white shadow-sm' 
                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700/60'
                            }`}
                        >
                            <History className="w-4 h-4" />
                            İşlem Geçmişi & Geri Alma
                        </button>
                    </div>

                    {activeTab === 'new' ? (
                        <div className="flex-1 overflow-y-auto pr-1">
                            {/* ALT SEKMELER (5 OPERASYON TİPİ) */}
                            <div className="grid grid-cols-2 sm:grid-cols-5 gap-1.5 mb-6 bg-gray-100 dark:bg-gray-900 p-1.5 rounded-xl border border-gray-200 dark:border-gray-700">
                                {canManual && (
                                    <button
                                        onClick={() => setOperationType('manual')}
                                        className={`py-2 px-2 rounded-lg font-semibold text-xs flex items-center justify-center gap-1.5 transition-all ${
                                            operationType === 'manual' 
                                            ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' 
                                            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                                        }`}
                                    >
                                        <Edit3 className="w-3.5 h-3.5 shrink-0" />
                                        Manuel Kayıt
                                    </button>
                                )}
                                {canLeaves && (
                                    <button
                                        onClick={() => setOperationType('leave')}
                                        className={`py-2 px-2 rounded-lg font-semibold text-xs flex items-center justify-center gap-1.5 transition-all ${
                                            operationType === 'leave' 
                                            ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' 
                                            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                                        }`}
                                    >
                                        <Calendar className="w-3.5 h-3.5 shrink-0" />
                                        Toplu İzin
                                    </button>
                                )}
                                {canScheduleOps && (
                                    <button
                                        onClick={() => setOperationType('workSchedule')}
                                        className={`py-2 px-2 rounded-lg font-semibold text-xs flex items-center justify-center gap-1.5 transition-all ${
                                            operationType === 'workSchedule' 
                                            ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' 
                                            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                                        }`}
                                    >
                                        <Briefcase className="w-3.5 h-3.5 shrink-0" />
                                        Mesai Atama
                                    </button>
                                )}
                                {canAccessOps && (
                                    <button
                                        onClick={() => setOperationType('access')}
                                        className={`py-2 px-2 rounded-lg font-semibold text-xs flex items-center justify-center gap-1.5 transition-all ${
                                            operationType === 'access' 
                                            ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' 
                                            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                                        }`}
                                    >
                                        <ShieldAlert className="w-3.5 h-3.5 shrink-0" />
                                        Kapı Yetkisi
                                    </button>
                                )}
                                {canAccessOps && (
                                    <button
                                        onClick={() => setOperationType('credentials')}
                                        className={`py-2 px-2 rounded-lg font-semibold text-xs flex items-center justify-center gap-1.5 transition-all ${
                                            operationType === 'credentials' 
                                            ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' 
                                            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                                        }`}
                                    >
                                        <CreditCard className="w-3.5 h-3.5 shrink-0" />
                                        Sanal Kart
                                    </button>
                                )}
                            </div>

                            {/* 1. MANUEL KAYIT FORMU */}
                            {operationType === 'manual' && canManual && (
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Geçiş Zamanı *</label>
                                        <input 
                                            type="datetime-local" 
                                            value={manualForm.recordTime}
                                            onChange={e => setManualForm({...manualForm, recordTime: e.target.value})}
                                            className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-xl px-3 py-2 text-gray-900 dark:text-gray-100 focus:outline-none"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Hareket Yönü *</label>
                                        <div className="grid grid-cols-2 gap-3">
                                            {/* GİRİŞ (YEŞİL) */}
                                            <button
                                                type="button"
                                                onClick={() => setManualForm({ ...manualForm, recordType: 0 })}
                                                className={`relative flex items-center gap-3.5 p-3.5 rounded-xl border-2 text-left transition-all duration-200 ${
                                                    manualForm.recordType === 0
                                                        ? 'bg-emerald-50 dark:bg-emerald-950/35 border-emerald-500 shadow-md shadow-emerald-500/10 ring-2 ring-emerald-500/20'
                                                        : 'bg-gray-50/70 dark:bg-gray-900/60 border-gray-200 dark:border-gray-700 hover:border-emerald-400/60 dark:hover:border-emerald-500/50 opacity-75 hover:opacity-100'
                                                }`}
                                            >
                                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
                                                    manualForm.recordType === 0
                                                        ? 'bg-emerald-600 text-white shadow-sm shadow-emerald-600/30'
                                                        : 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400'
                                                }`}>
                                                    <LogIn className="w-5 h-5" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className={`text-sm font-bold flex items-center gap-1.5 ${
                                                        manualForm.recordType === 0
                                                            ? 'text-emerald-800 dark:text-emerald-300'
                                                            : 'text-gray-800 dark:text-gray-200'
                                                    }`}>
                                                        Giriş (Entry)
                                                    </div>
                                                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                                                        Mesai başlangıç / turnike girişi
                                                    </p>
                                                </div>
                                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                                                    manualForm.recordType === 0
                                                        ? 'border-emerald-600 bg-emerald-600 text-white'
                                                        : 'border-gray-300 dark:border-gray-600'
                                                }`}>
                                                    {manualForm.recordType === 0 && <CheckCircle className="w-3.5 h-3.5" />}
                                                </div>
                                            </button>

                                            {/* ÇIKIŞ (KIRMIZI) */}
                                            <button
                                                type="button"
                                                onClick={() => setManualForm({ ...manualForm, recordType: 1 })}
                                                className={`relative flex items-center gap-3.5 p-3.5 rounded-xl border-2 text-left transition-all duration-200 ${
                                                    manualForm.recordType === 1
                                                        ? 'bg-red-50 dark:bg-red-950/35 border-red-500 shadow-md shadow-red-500/10 ring-2 ring-red-500/20'
                                                        : 'bg-gray-50/70 dark:bg-gray-900/60 border-gray-200 dark:border-gray-700 hover:border-red-400/60 dark:hover:border-red-500/50 opacity-75 hover:opacity-100'
                                                }`}
                                            >
                                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
                                                    manualForm.recordType === 1
                                                        ? 'bg-red-600 text-white shadow-sm shadow-red-600/30'
                                                        : 'bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400'
                                                }`}>
                                                    <LogOut className="w-5 h-5" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className={`text-sm font-bold flex items-center gap-1.5 ${
                                                        manualForm.recordType === 1
                                                            ? 'text-red-800 dark:text-red-300'
                                                            : 'text-gray-800 dark:text-gray-200'
                                                    }`}>
                                                        Çıkış (Exit)
                                                    </div>
                                                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                                                        Mesai bitiş / turnike çıkışı
                                                    </p>
                                                </div>
                                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                                                    manualForm.recordType === 1
                                                        ? 'border-red-600 bg-red-600 text-white'
                                                        : 'border-gray-300 dark:border-gray-600'
                                                }`}>
                                                    {manualForm.recordType === 1 && <CheckCircle className="w-3.5 h-3.5" />}
                                                </div>
                                            </button>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Cihaz / Kaynak Etiketi</label>
                                        <input 
                                            type="text" 
                                            value={manualForm.deviceSerialNumber}
                                            onChange={e => setManualForm({...manualForm, deviceSerialNumber: e.target.value})}
                                            placeholder="Sistem-Toplu"
                                            className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-xl px-3 py-2 text-gray-900 dark:text-gray-100 focus:outline-none"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Yasal Açıklama / Sebep * (En az 3 karakter)</label>
                                        <textarea 
                                            rows={3}
                                            value={manualForm.notes}
                                            onChange={e => setManualForm({...manualForm, notes: e.target.value})}
                                            placeholder="Örn: Elektrik kesintisi / Servis gecikmesi sebebiyle toplu giriş eklendi..."
                                            className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-xl p-3 text-gray-900 dark:text-gray-100 focus:outline-none"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* 2. TOPLU İZİN FORMU */}
                            {operationType === 'leave' && canLeaves && (
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">İzin Türü *</label>
                                        <select 
                                            value={leaveForm.izinTuruId}
                                            onChange={e => setLeaveForm({...leaveForm, izinTuruId: e.target.value})}
                                            className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-xl px-3 py-2 text-gray-900 dark:text-gray-100 focus:outline-none"
                                        >
                                            <option value="">Seçiniz...</option>
                                            {leaveTypes.map(lt => (
                                                <option key={lt.id} value={lt.id}>{lt.ad}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {selectedLeaveTypeObj && selectedLeaveTypeObj.kullanimBirimi === 3 && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Kullanım Şekli *</label>
                                            <div className="flex gap-4">
                                                <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-800 dark:text-gray-200">
                                                    <input 
                                                        type="radio" 
                                                        checked={leaveForm.useUnit === 1} 
                                                        onChange={() => setLeaveForm({...leaveForm, useUnit: 1})}
                                                        className="text-emerald-600"
                                                    />
                                                    <span>Tam Gün İzin</span>
                                                </label>
                                                <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-800 dark:text-gray-200">
                                                    <input 
                                                        type="radio" 
                                                        checked={leaveForm.useUnit === 2} 
                                                        onChange={() => setLeaveForm({...leaveForm, useUnit: 2})}
                                                        className="text-emerald-600"
                                                    />
                                                    <span>Saatlik İzin</span>
                                                </label>
                                            </div>
                                        </div>
                                    )}

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                {leaveForm.useUnit === 2 ? 'İzin Tarihi *' : 'Başlangıç Tarihi *'}
                                            </label>
                                            <input 
                                                type="date" 
                                                value={leaveForm.startDate}
                                                onChange={e => setLeaveForm({...leaveForm, startDate: e.target.value})}
                                                className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-xl px-3 py-2 text-gray-900 dark:text-gray-100 focus:outline-none"
                                            />
                                        </div>
                                        
                                        {leaveForm.useUnit === 1 && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Bitiş Tarihi *</label>
                                                <input 
                                                    type="date" 
                                                    value={leaveForm.endDate}
                                                    onChange={e => setLeaveForm({...leaveForm, endDate: e.target.value})}
                                                    className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-xl px-3 py-2 text-gray-900 dark:text-gray-100 focus:outline-none"
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {leaveForm.useUnit === 2 && (
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Başlangıç Saati *</label>
                                                <input 
                                                    type="time" 
                                                    value={leaveForm.startTime}
                                                    onChange={e => setLeaveForm({...leaveForm, startTime: e.target.value})}
                                                    className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-xl px-3 py-2 text-gray-900 dark:text-gray-100 focus:outline-none"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Bitiş Saati *</label>
                                                <input 
                                                    type="time" 
                                                    value={leaveForm.endTime}
                                                    onChange={e => setLeaveForm({...leaveForm, endTime: e.target.value})}
                                                    className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-xl px-3 py-2 text-gray-900 dark:text-gray-100 focus:outline-none"
                                                />
                                            </div>
                                        </div>
                                    )}

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Açıklama * (En az 3 karakter)</label>
                                        <textarea 
                                            rows={3}
                                            value={leaveForm.aciklama}
                                            onChange={e => setLeaveForm({...leaveForm, aciklama: e.target.value})}
                                            placeholder="Örn: Fabrika bakım çalışması / Bayram birleştirme toplu izin..."
                                            className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-xl p-3 text-gray-900 dark:text-gray-100 focus:outline-none"
                                        />
                                    </div>

                                    <div className="pt-2">
                                        <label className="flex items-center gap-3 cursor-pointer p-3.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/60 hover:bg-gray-100 dark:hover:bg-gray-700">
                                            <input 
                                                type="checkbox" 
                                                checked={leaveForm.autoApprove}
                                                onChange={e => setLeaveForm({...leaveForm, autoApprove: e.target.checked})}
                                                className="w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-emerald-600"
                                            />
                                            <div>
                                                <span className="block text-sm font-medium text-gray-900 dark:text-white">Otomatik Onayla ve Bakiyeden Düş (Ledger Kayıtlı)</span>
                                                <span className="block text-xs text-gray-500 dark:text-gray-400">
                                                    İşaretlenirse onay adımları atlanır; her personelin kendi mesai süresine göre bakiyesi düşülür ve İzin Bakiye Hareket (Ekstre) tablosuna işlenir.
                                                </span>
                                            </div>
                                        </label>
                                    </div>
                                </div>
                            )}

                            {/* 3. TOPLU MESAİ / ÇALIŞMA PERİYODU ATAMA FORMU */}
                            {operationType === 'workSchedule' && canScheduleOps && (
                                <div className="space-y-4">
                                    <div className="p-3.5 bg-amber-50 dark:bg-amber-900/15 border border-amber-200 dark:border-amber-500/25 rounded-xl text-amber-900 dark:text-amber-200 text-xs flex gap-2.5">
                                        <Briefcase className="w-5 h-5 shrink-0 text-amber-600 dark:text-amber-400" />
                                        <div>
                                            <strong>Esnek Çalışma Periyodu Ataması:</strong> Seçili personellere atanacak çalışma periyodu, önceki takvim kayıtlarıyla çakışıyorsa sistem mevcut aralıkları otomatik olarak böler/uyarlar ve geçmişe dönük tarih varsa puantajı yeniden hesaplar.
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Atanacak Çalışma Periyodu (Mesai Takvimi) *</label>
                                        <select
                                            value={scheduleForm.workPeriodId}
                                            onChange={e => setScheduleForm({ ...scheduleForm, workPeriodId: e.target.value })}
                                            className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-xl px-3 py-2 text-gray-900 dark:text-gray-100 focus:outline-none"
                                        >
                                            <option value="">Çalışma Periyodu Seçiniz...</option>
                                            {workPeriods.map(wp => (
                                                <option key={wp.id} value={wp.id}>
                                                    {wp.name} {wp.hasDynamicDayOff ? `(Dinamik HT: Haftada ${wp.dynamicDayOffCount || 1} gün)` : ''}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {selectedWorkPeriodObj && (
                                        <div className="p-3.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl">
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">Haftalık Vardiya Özeti: {selectedWorkPeriodObj.name}</span>
                                                {selectedWorkPeriodObj.hasDynamicDayOff && (
                                                    <span className="text-[11px] px-2 py-0.5 bg-amber-100 dark:bg-amber-500/15 text-amber-800 dark:text-amber-400 rounded border border-amber-200 dark:border-amber-500/30">
                                                        Dinamik Hafta Tatili ({selectedWorkPeriodObj.dynamicDayOffCount || 1} Gün)
                                                    </span>
                                                )}
                                            </div>
                                            <div className="grid grid-cols-7 gap-1 text-center text-[11px]">
                                                {[
                                                    { d: 'Pzt', v: selectedWorkPeriodObj.monday || selectedWorkPeriodObj.mondayShiftGroup?.name || 'Tatil' },
                                                    { d: 'Sal', v: selectedWorkPeriodObj.tuesday || selectedWorkPeriodObj.tuesdayShiftGroup?.name || 'Tatil' },
                                                    { d: 'Çar', v: selectedWorkPeriodObj.wednesday || selectedWorkPeriodObj.wednesdayShiftGroup?.name || 'Tatil' },
                                                    { d: 'Per', v: selectedWorkPeriodObj.thursday || selectedWorkPeriodObj.thursdayShiftGroup?.name || 'Tatil' },
                                                    { d: 'Cum', v: selectedWorkPeriodObj.friday || selectedWorkPeriodObj.fridayShiftGroup?.name || 'Tatil' },
                                                    { d: 'Cmt', v: selectedWorkPeriodObj.saturday || selectedWorkPeriodObj.saturdayShiftGroup?.name || 'Tatil' },
                                                    { d: 'Paz', v: selectedWorkPeriodObj.sunday || selectedWorkPeriodObj.sundayShiftGroup?.name || 'Tatil' },
                                                ].map(item => (
                                                    <div key={item.d} className="bg-white dark:bg-gray-800 p-1.5 rounded-lg border border-gray-200 dark:border-gray-700">
                                                        <div className="text-gray-500 dark:text-gray-400 font-bold">{item.d}</div>
                                                        <div className={`truncate mt-0.5 font-medium ${item.v === 'Tatil' ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'}`} title={item.v}>
                                                            {item.v}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Atama Süresi Tipi *</label>
                                        <div className="grid grid-cols-2 gap-3">
                                            <label className={`flex items-start gap-2.5 p-3 rounded-xl border cursor-pointer transition-colors ${
                                                scheduleForm.isPermanent ? 'bg-amber-50 dark:bg-amber-500/15 border-amber-400 dark:border-amber-500 text-gray-900 dark:text-white' : 'bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300'
                                            }`}>
                                                <input
                                                    type="radio"
                                                    checked={scheduleForm.isPermanent}
                                                    onChange={() => setScheduleForm({ ...scheduleForm, isPermanent: true })}
                                                    className="mt-1"
                                                />
                                                <div>
                                                    <span className="block text-sm font-medium">Kalıcı (Süresiz) Atama</span>
                                                    <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">Başlangıç tarihinden itibaren yeni bir atama yapılana kadar geçerli olur.</span>
                                                </div>
                                            </label>
                                            <label className={`flex items-start gap-2.5 p-3 rounded-xl border cursor-pointer transition-colors ${
                                                !scheduleForm.isPermanent ? 'bg-amber-50 dark:bg-amber-500/15 border-amber-400 dark:border-amber-500 text-gray-900 dark:text-white' : 'bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300'
                                            }`}>
                                                <input
                                                    type="radio"
                                                    checked={!scheduleForm.isPermanent}
                                                    onChange={() => setScheduleForm({ ...scheduleForm, isPermanent: false })}
                                                    className="mt-1"
                                                />
                                                <div>
                                                    <span className="block text-sm font-medium">Geçici (Tarih Aralıklı) Atama</span>
                                                    <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">Sadece seçilen başlangıç ve bitiş tarihleri arasında geçerli olur.</span>
                                                </div>
                                            </label>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Başlangıç Tarihi *</label>
                                            <input
                                                type="date"
                                                value={scheduleForm.startDate}
                                                onChange={e => setScheduleForm({ ...scheduleForm, startDate: e.target.value })}
                                                className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-xl px-3 py-2 text-gray-900 dark:text-gray-100 focus:outline-none"
                                            />
                                        </div>
                                        {!scheduleForm.isPermanent && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Bitiş Tarihi *</label>
                                                <input
                                                    type="date"
                                                    value={scheduleForm.endDate}
                                                    onChange={e => setScheduleForm({ ...scheduleForm, endDate: e.target.value })}
                                                    className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-xl px-3 py-2 text-gray-900 dark:text-gray-100 focus:outline-none"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* 4. TOPLU YETKİ FORMU */}
                            {operationType === 'access' && canAccessOps && (
                                <div className="space-y-4">
                                    <div className="p-4 bg-emerald-50 dark:bg-emerald-900/15 border border-emerald-200 dark:border-emerald-500/25 rounded-xl text-emerald-900 dark:text-emerald-200 text-xs flex gap-3">
                                        <Info className="w-5 h-5 shrink-0 text-emerald-600 dark:text-emerald-400" />
                                        <div>
                                            Seçili personellerin cihaz ve kapı geçiş yetkilerini toplu olarak günceller. <strong>"Eski Yetkileri Sil, Bunu Ata"</strong> seçildiğinde eski gruplardaki cihazlara otomatik olarak <code>CardDelete</code> (Kart Silme) komutu da gönderilir.
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Atanacak Yetki Grubu *</label>
                                        <select 
                                            value={accessForm.accessGroupId}
                                            onChange={e => setAccessForm({...accessForm, accessGroupId: e.target.value})}
                                            className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-xl px-3 py-2 text-gray-900 dark:text-gray-100 focus:outline-none"
                                        >
                                            <option value="">Seçiniz...</option>
                                            {accessGroups.map(ag => (
                                                <option key={ag.id} value={ag.id}>{ag.name}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Atama Yöntemi *</label>
                                        <div className="grid grid-cols-2 gap-3">
                                            <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                                                accessForm.assignmentType === 2 ? 'bg-emerald-50 dark:bg-emerald-600/20 border-emerald-400 dark:border-emerald-500 text-gray-900 dark:text-white' : 'bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300'
                                            }`}>
                                                <input 
                                                    type="radio" 
                                                    checked={accessForm.assignmentType === 2} 
                                                    onChange={() => setAccessForm({...accessForm, assignmentType: 2})}
                                                    className="mt-1 text-emerald-600"
                                                />
                                                <div>
                                                    <span className="block text-sm font-medium">Mevcut Yetkilere Ekle</span>
                                                    <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">Önceki yetki gruplarına dokunmaz, seçili grubu ilave eder.</span>
                                                </div>
                                            </label>

                                            <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                                                accessForm.assignmentType === 1 ? 'bg-red-50 dark:bg-red-900/20 border-red-400 dark:border-red-500 text-gray-900 dark:text-white' : 'bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300'
                                            }`}>
                                                <input 
                                                    type="radio" 
                                                    checked={accessForm.assignmentType === 1} 
                                                    onChange={() => setAccessForm({...accessForm, assignmentType: 1})}
                                                    className="mt-1 text-red-600"
                                                />
                                                <div>
                                                    <span className="block text-sm font-medium">Eski Yetkileri Sil, Bunu Ata</span>
                                                    <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">Önceki yetkileri iptal eder, eski cihazlardan kartları siler ve sadece bu grubu tanımlar.</span>
                                                </div>
                                            </label>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Personel Çakışma Öncelik Modu</label>
                                        <select 
                                            value={accessForm.priorityMode}
                                            onChange={e => setAccessForm({...accessForm, priorityMode: Number(e.target.value)})}
                                            className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-xl px-3 py-2 text-gray-900 dark:text-gray-100 focus:outline-none"
                                        >
                                            <option value={-1}>Mevcut Ayarlara Dokunma</option>
                                            <option value={0}>Yetkili Baskın (Bir grupta bile yetkiliyse geçebilir)</option>
                                            <option value={1}>Yasaklı Baskın (Bir grupta bile yasaklıysa geçemez)</option>
                                        </select>
                                    </div>
                                </div>
                            )}

                            {/* 5. TOPLU SANAL KART ÜRETİMİ FORMU */}
                            {operationType === 'credentials' && canAccessOps && (
                                <div className="space-y-4">
                                    <div className="p-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-700 dark:text-gray-300 text-xs flex gap-3">
                                        <Info className="w-5 h-5 shrink-0 text-gray-600 dark:text-gray-400" />
                                        <div>
                                            Seçili personellerin mobil uygulamada kullanabilmesi için otomatik olarak sanal kart (QR veya NFC) numarası üretir ve yetkili oldukları cihazlara gönderir.
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Üretilecek Kart Tipi *</label>
                                        <div className="grid grid-cols-2 gap-3">
                                            <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                                                credentialForm.credentialType === 5 ? 'bg-emerald-50 dark:bg-emerald-600/20 border-emerald-400 dark:border-emerald-500 text-gray-900 dark:text-white' : 'bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300'
                                            }`}>
                                                <input 
                                                    type="radio" 
                                                    checked={credentialForm.credentialType === 5} 
                                                    onChange={() => setCredentialForm({...credentialForm, credentialType: 5})}
                                                    className="mt-1 text-emerald-600"
                                                />
                                                <div>
                                                    <span className="block text-sm font-medium">Sanal QR Kartı</span>
                                                    <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">Mobil uygulamada dinamik QR Kod okutarak geçiş yapılmasını sağlar.</span>
                                                </div>
                                            </label>

                                            <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                                                credentialForm.credentialType === 6 ? 'bg-emerald-50 dark:bg-emerald-600/20 border-emerald-400 dark:border-emerald-500 text-gray-900 dark:text-white' : 'bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300'
                                            }`}>
                                                <input 
                                                    type="radio" 
                                                    checked={credentialForm.credentialType === 6} 
                                                    onChange={() => setCredentialForm({...credentialForm, credentialType: 6})}
                                                    className="mt-1 text-emerald-600"
                                                />
                                                <div>
                                                    <span className="block text-sm font-medium">Sanal NFC Kartı</span>
                                                    <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">Telefonun NFC özelliği ile fiziksel kart gibi okutularak geçiş yapılmasını sağlar.</span>
                                                </div>
                                            </label>
                                        </div>
                                    </div>

                                    <div className="pt-2">
                                        <label className="flex items-center gap-3 cursor-pointer p-3.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/60 hover:bg-gray-100 dark:hover:bg-gray-700">
                                            <input 
                                                type="checkbox" 
                                                checked={credentialForm.overwriteExisting}
                                                onChange={e => setCredentialForm({...credentialForm, overwriteExisting: e.target.checked})}
                                                className="w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-emerald-600"
                                            />
                                            <div>
                                                <span className="block text-sm font-medium text-gray-900 dark:text-white">Mevcut Kartı Olanların Üzerine Yaz (Eskisini İptal Et)</span>
                                                <span className="block text-xs text-gray-500 dark:text-gray-400">İşaretlenirse, personelin önceden tanımlı sanal kartı silinip yenisi üretilir. İşaretlenmezse mevcut kartı olanlar atlanır.</span>
                                            </div>
                                        </label>
                                    </div>
                                </div>
                            )}

                            {/* CANLI YÜZDELİK İLERLEME ÇUBUĞU (INLINE DURUM KARTI) */}
                            {liveProgress && (
                                <div className="mt-6 p-4 rounded-2xl bg-gray-50 dark:bg-gray-900 border border-emerald-300/80 dark:border-emerald-600/40 shadow-sm space-y-3 transition-all duration-300">
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="flex items-center gap-2.5 min-w-0">
                                            <div className="w-8 h-8 rounded-xl bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
                                                {liveProgress.percent >= 100 ? (
                                                    <CheckCircle className="w-4 h-4" />
                                                ) : (
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                )}
                                            </div>
                                            <div className="min-w-0">
                                                <div className="text-sm font-bold text-gray-900 dark:text-white truncate">
                                                    {liveProgress.stageTitle}
                                                </div>
                                                <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                                    {liveProgress.stageDetail}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="shrink-0 text-right">
                                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-extrabold bg-emerald-600 text-white shadow-sm tabular-nums">
                                                %{liveProgress.percent} Tamamlandı
                                            </span>
                                        </div>
                                    </div>

                                    {/* Zarif Yüzdelik İlerleme Çubuğu */}
                                    <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden p-0.5">
                                        <div
                                            className="h-full rounded-full bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-400 transition-all duration-300 ease-out"
                                            style={{ width: `${Math.max(5, Math.min(100, liveProgress.percent))}%` }}
                                        />
                                    </div>

                                    {/* Canlı Sayaçlar & Aşama Adımları */}
                                    <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-gray-600 dark:text-gray-300 pt-0.5">
                                        <div className="flex items-center gap-3">
                                            <span>
                                                İşlenen: <strong className="text-gray-900 dark:text-white tabular-nums">{liveProgress.processedCount} / {liveProgress.totalCount}</strong>
                                            </span>
                                            <span>•</span>
                                            <span className="text-emerald-600 dark:text-emerald-400">
                                                Başarılı: <strong className="tabular-nums">{liveProgress.successCount}</strong>
                                            </span>
                                            <span>•</span>
                                            <span className="text-red-600 dark:text-red-400">
                                                Hatalı/Atlanan: <strong className="tabular-nums">{liveProgress.failedCount}</strong>
                                            </span>
                                        </div>
                                        <span className="text-gray-400 dark:text-gray-500 font-medium">
                                            {liveProgress.percent < 25 ? 'Adım 1/4: Veri Hazırlığı' :
                                             liveProgress.percent < 85 ? 'Adım 2/4: Personel & Kural Kontrolü' :
                                             liveProgress.percent < 98 ? 'Adım 3/4: Veritabanı & Ekstre Kaydı' :
                                             'Adım 4/4: Tamamlandı'}
                                        </span>
                                    </div>
                                </div>
                            )}

                            {/* AKSİYON BUTONLARI: ÖN KONTROL (SİMÜLASYON) & UYGULA */}
                            <div className="mt-8 pt-4 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                                <button
                                    type="button"
                                    onClick={() => handleExecute(true)}
                                    disabled={submitting || selectedEmpIds.length === 0}
                                    className="bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 border border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-100 px-4 py-2.5 rounded-xl font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                >
                                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4 text-gray-600 dark:text-gray-300" />}
                                    Ön Kontrol Yap (Simülasyon)
                                </button>

                                <button 
                                    type="button"
                                    onClick={handlePreSubmit}
                                    disabled={submitting || selectedEmpIds.length === 0}
                                    className="bg-emerald-600 hover:bg-emerald-500 text-white px-6 py-2.5 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-emerald-900/20 text-sm"
                                >
                                    {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                                    Toplu İşlemi Uygula ({selectedEmpIds.length} Kişi)
                                </button>
                            </div>
                        </div>
                    ) : (
                        /* İŞLEM GEÇMİŞİ VE GERİ ALMA (UNDO) LİSTESİ */
                        <div className="flex-1 flex flex-col min-h-0">
                            {historyLoading ? (
                                <div className="flex-1 flex items-center justify-center">
                                    <Loader2 className="w-8 h-8 text-gray-500 animate-spin" />
                                </div>
                            ) : history.length === 0 ? (
                                <div className="flex-1 flex flex-col items-center justify-center text-gray-400 dark:text-gray-500">
                                    <History className="w-12 h-12 mb-2 opacity-30" />
                                    <p>Henüz kaydedilmiş bir toplu işlem geçmişi bulunmuyor.</p>
                                </div>
                            ) : (
                                <>
                                    <div className="flex-1 overflow-y-auto pr-1">
                                        <table className="w-full text-left border-collapse">
                                            <thead>
                                                <tr className="border-b border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 uppercase">
                                                    <th className="py-3 px-2 cursor-pointer hover:text-gray-900 dark:hover:text-white select-none" onClick={() => handleHistorySort('OperationType')}>
                                                        <div className="flex items-center gap-1">
                                                            İşlem Tipi
                                                            {historySortConfig.key === 'OperationType' && (historySortConfig.direction === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
                                                        </div>
                                                    </th>
                                                    <th className="py-3 px-2 cursor-pointer hover:text-gray-900 dark:hover:text-white select-none" onClick={() => handleHistorySort('CreatedByUserName')}>
                                                        <div className="flex items-center gap-1">
                                                            Yapan
                                                            {historySortConfig.key === 'CreatedByUserName' && (historySortConfig.direction === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
                                                        </div>
                                                    </th>
                                                    <th className="py-3 px-2 cursor-pointer hover:text-gray-900 dark:hover:text-white select-none" onClick={() => handleHistorySort('CreatedAt')}>
                                                        <div className="flex items-center gap-1">
                                                            Tarih
                                                            {historySortConfig.key === 'CreatedAt' && (historySortConfig.direction === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
                                                        </div>
                                                    </th>
                                                    <th className="py-3 px-2 text-center">Özet</th>
                                                    <th className="py-3 px-2 text-center cursor-pointer hover:text-gray-900 dark:hover:text-white select-none" onClick={() => handleHistorySort('Status')}>
                                                        <div className="flex items-center justify-center gap-1">
                                                            Durum
                                                            {historySortConfig.key === 'Status' && (historySortConfig.direction === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
                                                        </div>
                                                    </th>
                                                    <th className="py-3 px-2 text-right">İşlem</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60 text-sm">
                                                {history.map(item => (
                                                    <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors">
                                                        <td className="py-3 px-2">
                                                            {getOperationTypeBadge(item.operationType)}
                                                        </td>
                                                        <td className="py-3 px-2 text-gray-800 dark:text-gray-200 font-medium">{item.createdByUserName}</td>
                                                        <td className="py-3 px-2 text-gray-500 dark:text-gray-400 text-xs">
                                                            {new Date(item.createdAt).toLocaleString('tr-TR')}
                                                        </td>
                                                        <td className="py-3 px-2 text-center">
                                                            <div className="flex items-center justify-center gap-1.5 text-xs">
                                                                <span className="text-gray-600 dark:text-gray-300" title="Toplam">{item.totalCount}</span>
                                                                <span>/</span>
                                                                <span className="text-emerald-600 dark:text-emerald-400 font-bold" title="Başarılı">{item.successCount}</span>
                                                                <span>/</span>
                                                                <span className="text-red-600 dark:text-red-400 font-bold" title="Hatalı">{item.failedCount}</span>
                                                            </div>
                                                        </td>
                                                        <td className="py-3 px-2 text-center">
                                                            {getHistoryStatusBadge(item.status)}
                                                        </td>
                                                        <td className="py-3 px-2 text-right">
                                                            <div className="flex items-center justify-end gap-1.5">
                                                                {canUndoHistoryItem(item) && (
                                                                    <button
                                                                        onClick={() => handleUndoOperation(item)}
                                                                        disabled={undoingId === item.id}
                                                                        title="Bu toplu işlemi geri al (kayıtları iptal et ve bakiyeleri iade et)"
                                                                        className="px-2 py-1 rounded-lg bg-orange-50 hover:bg-orange-100 dark:bg-orange-500/10 dark:hover:bg-orange-500/20 text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-orange-500/25 text-xs flex items-center gap-1 transition-colors disabled:opacity-50"
                                                                    >
                                                                        {undoingId === item.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                                                                        Geri Al
                                                                    </button>
                                                                )}
                                                                <button 
                                                                    onClick={() => fetchHistoryDetail(item.id)}
                                                                    title="Detayları İncele"
                                                                    className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                                                                >
                                                                    <ChevronRight className="w-5 h-5" />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    
                                    {/* History Pagination Footer */}
                                    <div className="pt-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mt-2">
                                        <div className="flex items-center gap-2">
                                            <span>Sayfa Başına:</span>
                                            <select 
                                                value={historyPageSize} 
                                                onChange={e => { setHistoryPageSize(Number(e.target.value)); setHistoryPage(1); }}
                                                className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1 text-gray-800 dark:text-gray-200 focus:outline-none"
                                            >
                                                <option value={10}>10</option>
                                                <option value={20}>20</option>
                                                <option value={50}>50</option>
                                                <option value={100}>100</option>
                                            </select>
                                            <span className="ml-2">Toplam {historyTotal} kayıt</span>
                                        </div>
                                        
                                        <div className="flex items-center gap-2">
                                            <button 
                                                disabled={historyPage === 1}
                                                onClick={() => setHistoryPage(p => Math.max(1, p - 1))}
                                                className="px-2.5 py-1 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
                                            >
                                                Önceki
                                            </button>
                                            <span>
                                                Sayfa <strong className="text-gray-900 dark:text-white">{historyPage}</strong> / {Math.ceil(historyTotal / historyPageSize) || 1}
                                            </span>
                                            <button 
                                                disabled={historyPage >= (Math.ceil(historyTotal / historyPageSize) || 1)}
                                                onClick={() => setHistoryPage(p => p + 1)}
                                                className="px-2.5 py-1 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
                                            >
                                                Sonraki
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* LİSTE YAPIŞTIR (EXCEL'DEN SİCİL / TC HIZLI SEÇİM) MODALI */}
            {pasteModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl max-w-lg w-full p-6 shadow-2xl">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                <ClipboardPaste className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                                Excel veya Listeden Hızlı Personel Seçimi
                            </h3>
                            <button onClick={() => setPasteModalOpen(false)} className="text-gray-400 hover:text-gray-700 dark:hover:text-white">✕</button>
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-300 mb-3">
                            Excel tablosundan kopyaladığınız <strong>T.C. Kimlik No</strong>, <strong>Sicil No</strong> veya <strong>Ad Soyad</strong> listesini alt alta veya virgülle ayrılmış şekilde aşağıya yapıştırın:
                        </p>
                        <textarea
                            rows={6}
                            value={pasteText}
                            onChange={e => setPasteText(e.target.value)}
                            placeholder={"12345678901\n98765432109\nAhmet Yılmaz"}
                            className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-xl p-3 text-sm text-gray-900 dark:text-white font-mono focus:outline-none"
                        />

                        {pasteSummary && (
                            <div className="mt-3 p-3 rounded-xl bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-xs space-y-1">
                                <div className="flex justify-between">
                                    <span className="text-gray-500 dark:text-gray-400">Girilen Kayıt:</span>
                                    <strong className="text-gray-900 dark:text-white">{pasteSummary.totalInput}</strong>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-emerald-600 dark:text-emerald-400">Eşleşen ve Seçilen:</span>
                                    <strong className="text-emerald-600 dark:text-emerald-400">{pasteSummary.matchedCount}</strong>
                                </div>
                                {pasteSummary.unmatchedTokens.length > 0 && (
                                    <div className="pt-1 border-t border-gray-200 dark:border-gray-700 text-amber-600 dark:text-amber-400">
                                        Bulunamayan ({pasteSummary.unmatchedTokens.length}): {pasteSummary.unmatchedTokens.slice(0, 10).join(', ')}
                                        {pasteSummary.unmatchedTokens.length > 10 ? '...' : ''}
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="flex justify-end gap-2 mt-5">
                            <button
                                onClick={() => { setPasteText(''); setPasteSummary(null); }}
                                className="px-3 py-2 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 text-xs font-medium"
                            >
                                Kutuyu Temizle
                            </button>
                            <button
                                onClick={handleApplyPasteSelection}
                                className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-semibold text-xs"
                            >
                                Eşleşenleri Seç
                            </button>
                            <button
                                onClick={() => setPasteModalOpen(false)}
                                className="px-4 py-2 rounded-xl bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-white text-xs font-medium"
                            >
                                Kapat
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* İŞLEM GEÇMİŞİ DETAY MODALI */}
            {selectedHistory && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl max-w-4xl w-full p-6 shadow-2xl flex flex-col max-h-[90vh]">
                        <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 pb-4 mb-4">
                            <div>
                                <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                    İşlem Detayı #{selectedHistory.id}
                                    {getHistoryStatusBadge(selectedHistory.status)}
                                </h3>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    {selectedHistory.createdByUserName} tarafından {new Date(selectedHistory.createdAt).toLocaleString('tr-TR')} tarihinde gerçekleştirildi.
                                </p>
                            </div>
                            <button 
                                onClick={() => setSelectedHistory(null)}
                                className="text-gray-400 hover:text-gray-700 dark:hover:text-white p-1"
                            >✕</button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                            <div className="bg-gray-50 dark:bg-gray-900 p-3.5 rounded-xl border border-gray-200 dark:border-gray-700">
                                <span className="text-xs text-gray-500 dark:text-gray-400 block">İşlem Özeti</span>
                                <span className="text-sm font-medium text-gray-900 dark:text-white mt-1 block">{selectedHistory.requestSummary || 'Özet bilgi yok'}</span>
                            </div>
                            <div className="bg-gray-50 dark:bg-gray-900 p-3.5 rounded-xl border border-gray-200 dark:border-gray-700">
                                <span className="text-xs text-gray-500 dark:text-gray-400 block">Kayıt İstatistikleri</span>
                                <div className="flex items-center gap-3 mt-1">
                                    <span className="text-sm text-gray-900 dark:text-white">Toplam: <b>{selectedHistory.totalCount}</b></span>
                                    <span className="text-sm text-emerald-600 dark:text-emerald-400">Başarılı: <b>{selectedHistory.successCount}</b></span>
                                    <span className="text-sm text-red-600 dark:text-red-400">Hatalı: <b>{selectedHistory.failedCount}</b></span>
                                </div>
                            </div>
                            <div className="bg-gray-50 dark:bg-gray-900 p-3.5 rounded-xl border border-gray-200 dark:border-gray-700">
                                <span className="text-xs text-gray-500 dark:text-gray-400 block">Puantaj Hesaplama</span>
                                <span className={`text-sm font-medium mt-1 block ${selectedHistory.recalcTriggered ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
                                    {selectedHistory.recalcTriggered ? 'Otomatik Tetiklendi' : 'Tetiklenmedi'}
                                </span>
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50/50 dark:bg-gray-900/50">
                            <table className="w-full text-left border-collapse text-xs">
                                <thead className="sticky top-0 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-b border-gray-200 dark:border-gray-600">
                                    <tr>
                                        <th className="p-3">Personel</th>
                                        <th className="p-3">Durum</th>
                                        <th className="p-3">Etkilenen Tarih</th>
                                        <th className="p-3">Açıklama / Hata Mesajı</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                    {selectedHistory.items.map(item => (
                                        <tr key={item.id} className="hover:bg-gray-100/60 dark:hover:bg-gray-700/30">
                                            <td className="p-3 font-medium text-gray-900 dark:text-white">{item.employeeName} (#{item.employeeId})</td>
                                            <td className="p-3">
                                                {item.resultStatus === 'Success' ? (
                                                    <span className="text-emerald-600 dark:text-emerald-400 flex items-center gap-1 font-medium">
                                                        <CheckCircle className="w-3.5 h-3.5" /> Başarılı
                                                    </span>
                                                ) : (
                                                    <span className="text-red-600 dark:text-red-400 flex items-center gap-1 font-medium">
                                                        <XCircle className="w-3.5 h-3.5" /> Hatalı
                                                    </span>
                                                )}
                                            </td>
                                            <td className="p-3 text-gray-500 dark:text-gray-400">
                                                {item.affectedStartDate ? new Date(item.affectedStartDate).toLocaleDateString('tr-TR') : '-'}
                                                {item.affectedEndDate && item.affectedEndDate !== item.affectedStartDate ? ` - ${new Date(item.affectedEndDate).toLocaleDateString('tr-TR')}` : ''}
                                            </td>
                                            <td className="p-3 text-gray-700 dark:text-gray-300">
                                                {item.errorMessage ? (
                                                    <span className={item.resultStatus === 'Success' ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-600 dark:text-red-300'}>
                                                        {item.errorMessage} {item.errorCode ? `(${item.errorCode})` : ''}
                                                    </span>
                                                ) : '-'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="mt-6 flex flex-wrap justify-between items-center gap-3">
                            <div className="flex flex-wrap gap-2">
                                <button 
                                    onClick={() => handleExport(selectedHistory.id, 'excel')}
                                    className="px-4 py-2 bg-emerald-50 dark:bg-emerald-600/20 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/30 rounded-xl hover:bg-emerald-100 dark:hover:bg-emerald-600/30 font-medium text-sm flex items-center gap-2 transition-colors"
                                >
                                    <FileSpreadsheet className="w-4 h-4" />
                                    Excel İndir
                                </button>
                                <button 
                                    onClick={() => handleExport(selectedHistory.id, 'pdf')}
                                    className="px-4 py-2 bg-red-50 dark:bg-red-600/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-500/30 rounded-xl hover:bg-red-100 dark:hover:bg-red-600/30 font-medium text-sm flex items-center gap-2 transition-colors"
                                >
                                    <Download className="w-4 h-4" />
                                    PDF İndir
                                </button>
                                {canUndoHistoryItem(selectedHistory) && (
                                    <button
                                        onClick={() => handleUndoOperation(selectedHistory)}
                                        disabled={undoingId === selectedHistory.id}
                                        className="px-4 py-2 bg-orange-50 dark:bg-orange-600/20 text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-orange-500/30 rounded-xl hover:bg-orange-100 dark:hover:bg-orange-600/30 font-medium text-sm flex items-center gap-2 transition-colors disabled:opacity-50"
                                    >
                                        {undoingId === selectedHistory.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                                        İşlemi Geri Al (Undo)
                                    </button>
                                )}
                            </div>
                            <button 
                                onClick={() => setSelectedHistory(null)}
                                className="px-6 py-2 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-white rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 font-medium text-sm"
                            >
                                Kapat
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ONAY MODALI */}
            {confirmOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl max-w-md w-full p-6 shadow-2xl">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                            <AlertCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                            Toplu İşlem Onayı
                        </h3>
                        <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">
                            Seçili <strong className="text-gray-900 dark:text-white">{selectedEmpIds.length}</strong> personel için{' '}
                            <strong className="text-gray-900 dark:text-white">
                                {operationType === 'manual' ? 'Toplu Manuel Geçiş Kaydı' :
                                 operationType === 'leave' ? 'Toplu İzin Girişi' :
                                 operationType === 'workSchedule' ? 'Toplu Çalışma Periyodu Ataması' :
                                 operationType === 'access' ? 'Toplu Kapı/Cihaz Yetki Senkronizasyonu' :
                                 'Toplu Sanal Kart (QR/NFC) Üretimi'}
                            </strong>{' '}
                            işlemi uygulanacaktır. Onaylıyor musunuz?
                        </p>
                        <div className="flex justify-end gap-3">
                            <button 
                                onClick={() => setConfirmOpen(false)}
                                className="px-4 py-2 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 text-sm font-medium"
                            >
                                İptal
                            </button>
                            <button 
                                onClick={() => handleExecute(false)}
                                className="px-4 py-2 rounded-xl bg-emerald-600 text-white hover:bg-emerald-500 text-sm font-semibold"
                            >
                                Evet, Şimdi Uygula
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* SONUÇ & SİMÜLASYON RAPORU MODALI */}
            {resultData && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/65 p-4">
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl max-w-2xl w-full p-6 shadow-2xl flex flex-col max-h-[85vh]">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                <FileText className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                                {resultData.isDryRun ? 'Ön Kontrol (Simülasyon) Raporu' : 'İşlem Sonuç Raporu'}
                            </h3>
                            <button onClick={() => setResultData(null)} className="text-gray-400 hover:text-gray-700 dark:hover:text-white">✕</button>
                        </div>

                        {resultData.isDryRun && (
                            <div className="mb-4 p-3.5 bg-amber-50 dark:bg-amber-500/15 border border-amber-200 dark:border-amber-500/30 rounded-xl text-amber-900 dark:text-amber-200 text-xs flex items-center justify-between gap-3">
                                <div>
                                    <strong>SİMÜLASYON MODU:</strong> Bu kontrol sırasında veritabanına hiçbir kayıt yazılmadı. Uygun olan <strong>{resultData.success}</strong> personel için işlemi hemen uygulayabilirsiniz.
                                </div>
                                {resultData.success > 0 && (
                                    <button
                                        onClick={() => handleExecute(false)}
                                        disabled={submitting}
                                        className="shrink-0 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-lg text-xs shadow"
                                    >
                                        Şimdi Gerçek İşlemi Uygula ({resultData.success})
                                    </button>
                                )}
                            </div>
                        )}
                        
                        <div className="grid grid-cols-3 gap-3 mb-4 text-center">
                            <div 
                                onClick={() => setReportFilter('all')}
                                className={`bg-gray-50 dark:bg-gray-900 p-3 rounded-xl border cursor-pointer transition-all ${reportFilter === 'all' ? 'border-gray-500 ring-1 ring-gray-400/50' : 'border-gray-200 dark:border-gray-700 opacity-70 hover:opacity-100'}`}
                            >
                                <span className="block text-xs text-gray-500 dark:text-gray-400">Toplam Seçilen</span>
                                <span className="text-xl font-bold text-gray-900 dark:text-white">{resultData.total}</span>
                            </div>
                            <div 
                                onClick={() => setReportFilter('success')}
                                className={`bg-emerald-50 dark:bg-emerald-950/30 p-3 rounded-xl border cursor-pointer transition-all ${reportFilter === 'success' ? 'border-emerald-500 ring-1 ring-emerald-500/50' : 'border-emerald-200 dark:border-emerald-800/50 opacity-70 hover:opacity-100'}`}
                            >
                                <span className="block text-xs text-emerald-700 dark:text-emerald-400">{resultData.isDryRun ? 'Uygun (Başarılı Olacak)' : 'Başarılı'}</span>
                                <span className="text-xl font-bold text-emerald-700 dark:text-emerald-400">{resultData.success}</span>
                            </div>
                            <div 
                                onClick={() => setReportFilter('error')}
                                className={`bg-red-50 dark:bg-red-950/30 p-3 rounded-xl border cursor-pointer transition-all ${reportFilter === 'error' ? 'border-red-500 ring-1 ring-red-500/50' : 'border-red-200 dark:border-red-800/50 opacity-70 hover:opacity-100'}`}
                            >
                                <span className="block text-xs text-red-700 dark:text-red-400">{resultData.isDryRun ? 'Uygun Değil (Hatalı)' : 'Hatalı / Atlanan'}</span>
                                <span className="text-xl font-bold text-red-700 dark:text-red-400">{resultData.failed}</span>
                            </div>
                        </div>

                        {resultData.warning && (
                            <div className="mb-4 p-3 bg-amber-50 dark:bg-yellow-500/10 border border-amber-200 dark:border-yellow-500/30 rounded-xl text-amber-800 dark:text-yellow-300 text-xs flex gap-2 items-start">
                                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                                <span>{resultData.warning}</span>
                            </div>
                        )}

                        {resultData.errors && resultData.errors.length > 0 && (
                            <div className="flex-1 overflow-y-auto mb-4 border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-900 p-3">
                                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">
                                    {reportFilter === 'all' ? 'Tüm İşlem Detayları' : reportFilter === 'success' ? 'Başarılı / Uygun Kayıtlar' : 'Hata / Uyarı Detayları'}
                                </h4>
                                <div className="space-y-2">
                                    {resultData.errors
                                        .filter(err => {
                                            if (reportFilter === 'all') return true;
                                            if (reportFilter === 'success') return err.code === 'success';
                                            if (reportFilter === 'error') return err.code !== 'success';
                                            return true;
                                        })
                                        .map((err, idx) => (
                                        <div key={idx} className={`text-xs flex items-start gap-2 border-b border-gray-200 dark:border-gray-800 pb-2 last:border-0 ${err.code === 'success' ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-600 dark:text-red-300'}`}>
                                            {err.code === 'success' ? (
                                                <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                                            ) : (
                                                <XCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                                            )}
                                            <div>
                                                <strong className="text-gray-900 dark:text-gray-100">{err.employeeName || `Personel #${err.employeeId}`}:</strong> {err.message}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="flex justify-between items-center mt-auto pt-3 border-t border-gray-200 dark:border-gray-700">
                            <div className="flex gap-2">
                                {!resultData.isDryRun && resultData.historyId && (
                                    <>
                                        <button
                                            onClick={() => handleExport(resultData.historyId, 'excel')}
                                            className="px-3 py-2 bg-emerald-50 dark:bg-emerald-600/20 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/30 rounded-xl hover:bg-emerald-100 dark:hover:bg-emerald-600/30 text-xs font-medium flex items-center gap-1.5"
                                        >
                                            <FileSpreadsheet className="w-3.5 h-3.5" /> Excel Raporu
                                        </button>
                                        <button
                                            onClick={() => handleExport(resultData.historyId, 'pdf')}
                                            className="px-3 py-2 bg-red-50 dark:bg-red-600/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-500/30 rounded-xl hover:bg-red-100 dark:hover:bg-red-600/30 text-xs font-medium flex items-center gap-1.5"
                                        >
                                            <Download className="w-3.5 h-3.5" /> PDF Raporu
                                        </button>
                                    </>
                                )}
                            </div>
                            <button 
                                onClick={() => setResultData(null)}
                                className="px-5 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-800 dark:text-white text-sm font-medium rounded-xl"
                            >
                                Kapat
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default BulkOperationsPage;
